import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, message, Spin, Avatar, Input, Form } from 'antd';
import { AlipayOutlined, CrownOutlined} from '@ant-design/icons';
import { createAlipayOrder, queryOrderStatus, checkCoupon } from '@/apis/pay';
import { ProductType, PAYMENT_CHANNEL_NAMES, PLAN_OPTIONS, OrderStatus } from '@shared/constants/payment';
import VipFeatures from './VipFeatures';
import PlanSelection from './PlanSelection';
import './styles.less';
import { UserStore } from '@/store/user';

interface PayModalProps {
  visible: boolean | 'vip' | 'points';
  activePlan?: ProductType;
  user: UserStore;
  onClose: () => void;
  onSuccess?: (orderInfo: any) => void;
}

type Coupon = {
  code: string;
  type: string;
  value: number;
}

const isCreateVipOrder = (productType: ProductType): boolean => {
  // 双节活动不可用coupon
  // return false
  return [ProductType.ONE_MONTH_VIP_MEMBERSHIP, ProductType.THREE_MONTH_VIP_MEMBERSHIP, ProductType.ONE_YEAR_VIP_MEMBERSHIP].includes(productType);
}

const getDefaultPlan = (isVip: boolean, defaultPlan: ProductType): ProductType => {
  if (isVip) {
    return defaultPlan ?? ProductType.THREE_MONTH_VIP_MEMBERSHIP;
  } else {
    return ProductType.ORANGE_BEAN_PACKAGE_500;
  }
}

const PayModal: React.FC<PayModalProps> = ({ visible, activePlan, user, onClose, onSuccess }) => {
  const isVip = visible === 'vip' || visible === true;
  const [selectedPlan, setSelectedPlan] = useState<ProductType>(getDefaultPlan(isVip, activePlan));
  const [loading, setLoading] = useState(false);
  const [qrUrl, setQrUrl] = useState<string>('');
  const [orderId, setOrderId] = useState<string>('');
  const pollingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [showCouponInput, setShowCouponInput] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [coupon, setCoupon] = useState<{
    discountPrice: number,
    coupon: Coupon,
  }>(null);
  const [isCheckingCoupon, setIsCheckingCoupon] = useState(false);
  const [couponMessage, setCouponMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // 清除轮询定时器
  const clearPollingTimer = () => {
    if (pollingTimerRef.current) {
      clearInterval(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
  };

  // 轮询订单状态
  const startPollingOrderStatus = (orderIdToCheck: string) => {
    // 先清除之前的定时器
    clearPollingTimer();
    
    pollingTimerRef.current = setInterval(async () => {
      try {
        const response = await queryOrderStatus(orderIdToCheck);
        
        if (response.code === 0) {
          const { paymentStatus } = response.data;
          // 支付成功
          if (paymentStatus === OrderStatus.SUCCESS) {
            clearPollingTimer();
            message.success('支付成功！');
            onSuccess?.(response.data);
            handleClose();
          }
          // 支付失败或取消
          else if (paymentStatus === OrderStatus.CLOSED || paymentStatus === OrderStatus.FAILED) {
            clearPollingTimer();
            message.error('支付失败或已取消');
          }
          // 其他状态继续轮询
        }
      } catch (error) {
        console.error('查询订单状态失败:', error);
      }
    }, 3000);
  };

  // 重置状态
  const resetState = () => {
    setSelectedPlan(getDefaultPlan(isVip, activePlan));
    setLoading(false);
    setQrUrl('');
    setOrderId('');
    clearPollingTimer();
    setShowCouponInput(false);
    setCouponCode('');
    setCoupon(null);
    setCouponMessage(null);
  };

  // 关闭弹窗
  const handleClose = () => {
    resetState();
    onClose();
  };

  // 创建订单并获取支付二维码
  const createOrderAndQR = async (planType: ProductType, couponCode?: string) => {
    try {
      setLoading(true);
      setQrUrl(''); // 清空之前的二维码
      clearPollingTimer(); // 清除之前的轮询
      
      const response = await createAlipayOrder({
        productType: planType,
        couponCode,
      });

      if (response.code === 0) {
        setQrUrl(response.data.qrUrl);
        setOrderId(response.data.out_trade_no);
        
        // 开始轮询订单状态
        startPollingOrderStatus(response.data.out_trade_no);
      } else {
        message.error('生成支付二维码失败，请重试');
      }
    } catch (error) {
      console.error('生成支付二维码失败:', error);
      message.error('生成支付二维码失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 处理套餐选择变化
  const handlePlanSelect = (planType: ProductType) => {
    setSelectedPlan(planType);
    // 立即为新选择的套餐生成二维码
    createOrderAndQR(planType);
    setCoupon(null);
    setCouponMessage(null);
    setCouponCode('');
  };

  const handleCheckCoupon = async () => {
    if (!couponCode) {
      message.error('请输入优惠码');
      return;
    }
    setIsCheckingCoupon(true);
    setCouponMessage(null);
    try {
      const res = await checkCoupon(couponCode, selectedPlan);
      if (res.code === 0) {
        setCouponMessage({ type: 'success', text: '优惠码有效' });
        await createOrderAndQR(selectedPlan, couponCode);
        setCoupon(res.data);
      } else {
        setCouponMessage({ type: 'error', text: res.message });
        setCoupon(null);
      }
    } catch (error) {
      setCouponMessage({ type: 'error', text: '校验失败' });
    } finally {
      setIsCheckingCoupon(false);
    }
  }

  // 获取选中套餐信息
  const selectedPlanInfo = PLAN_OPTIONS.find(plan => plan.type === selectedPlan);

  // 弹窗打开时生成默认套餐的二维码
  useEffect(() => {
    if (visible) {
      const isVip = visible === 'vip' || visible === true;
      const defaultPlan = getDefaultPlan(isVip, activePlan);
      createOrderAndQR(defaultPlan);
      setSelectedPlan(defaultPlan);
    } else {
      resetState();
    }
  }, [visible]);

  // 组件卸载时清除定时器
  useEffect(() => {
    return () => {
      clearPollingTimer();
    };
  }, []);

  const getDiscountPrice = () => {
    if (coupon?.discountPrice) {
      return (
        <div>
         <p>优惠后金额：¥{coupon?.discountPrice / 100}</p>
         <p style={{ color: 'red', fontSize: '14px' }}>优惠码抵扣金额：¥{coupon.coupon.value}</p>
        </div>
      )
    }
    return <>¥{selectedPlanInfo?.price}</>;
  }

  const getUserDescription = () => {
    if (user?.vipLevel > 0) {
      return (<div className="vip-badge"><CrownOutlined style={{ marginRight: 4 }} />橙子8设计会员</div>)
    }
    return (<div className="status-text">普通用户</div>)
  }
  return (
    <Modal
      open={visible === 'vip' || visible === 'points' || visible === true}
      onCancel={handleClose}
      destroyOnClose={true}
      footer={null}
      width={840}
      centered
      className="pay-modal"
      maskClosable={false}
    >
      <div className="pay-modal-content">
        {/* 头部 */}
        <div className="pay-modal-header">
          <div className="user-info">
            <Avatar
              size="large"
              src={user.avatar}
            />
            <div className="user-details">
              <div className="username">{user.nickname}</div>
              <div className="user-status">
                {getUserDescription()}
              </div>
            </div>
          </div>
        </div>

        {/* 主要内容区域 - 左右结构 */}
        <div className="pay-modal-body">
          {/* 左侧：VIP特权介绍 */}
          <div className="left-section">
            <VipFeatures />
          </div>
          
          {/* 右侧：套餐选择 + 支付模块 */}
          <div className="right-section">
            {/* 套餐选择 */}
            <div className="plan-section">
              <PlanSelection
                defaultActiveTab={isVip ? 'vip' : 'points'}
                selectedPlan={selectedPlan}
                onPlanSelect={handlePlanSelect}
                loading={loading}
                onClose={handleClose}
                onTabChange={(tabKey: string) => {
                  if (tabKey === 'vip') {
                    handlePlanSelect(ProductType.THREE_MONTH_VIP_MEMBERSHIP);
                  } else if (tabKey === 'points') {
                    handlePlanSelect(ProductType.ORANGE_BEAN_PACKAGE_500);
                  }
                }}
              />
            </div>
            
            {/* 支付模块 */}
            <div className="payment-section">
              {/* 支付二维码 */}
              <div className="qr-code-section">
                {loading ? (
                  <div className="qr-loading">
                    <Spin size="large" />
                    <p>正在生成支付二维码...</p>
                  </div>
                ) : qrUrl ? (
                  <iframe
                    src={qrUrl}
                    width="145"
                    height="145"
                    frameBorder="0"
                    scrolling="no"
                    className="qr-iframe"
                  />
                ) : (
                  <div className="qr-error">
                    <p>二维码生成失败</p>
                    <Button 
                      size="small" 
                      onClick={() => createOrderAndQR(selectedPlan)}
                    >
                      重新生成
                    </Button>
                  </div>
                )}
              </div>

              {/* 支付信息 */}
              <div className="payment-info">
                <div className="payment-title">
                  {getDiscountPrice()}
                </div>
                <div className="payment-method">
                  <AlipayOutlined className='method-icon' />
                  <span>支付宝扫码开通</span>
                </div>
                
                {/* 支付说明 */}
                <div className="payment-notice">
                  <p>支付即视为您已同意 <a href="#" target="_blank">《橙子8设计会员服务协议》</a></p>
                </div>

                {isCreateVipOrder(selectedPlan) && (
                  <div className="payment-actions">
                    <Button type="link" onClick={() => setShowCouponInput(!showCouponInput)}>
                      我有优惠码
                    </Button>  
                  </div>)
                }
                {showCouponInput && (
                  <div className="coupon-input-section">
                    <Input
                      placeholder="请输入优惠码"
                      value={couponCode}
                      onChange={(e) => {
                        setCouponCode(e.target.value);
                        setCouponMessage(null);
                        setCoupon(null);
                      }}
                      style={{ width: 200, marginRight: 8 }}
                    />
                    <Button onClick={handleCheckCoupon} loading={isCheckingCoupon}>校验</Button>
                    {couponMessage && (
                      <div style={{ color: couponMessage.type === 'error' ? 'red' : 'green', marginTop: 5 }}>
                        {couponMessage.text}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default PayModal;